import React, { useState } from 'react';
import { requestPortalEntryLink } from '@/api/portal';
import { usePortalActions } from './portalActionsContext';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';

/**
 * Ревизия WEB-1152 (п.5, «мёртвая ручка»): форма входа в портал отмены по
 * почте. Бэкенд-контур (POST /public/handoff/portal-entry/request → письмо со
 * ссылкой ?code=) был построен целиком, но во фронте не существовало ни одной
 * точки вызова — у двери не было ручки.
 *
 * Рендерится ИЗ ПОРТАЛЬНЫХ БЛОКОВ, когда портал открыт без валидного входа
 * (нет ?guid= и нет живого ?code=) — то есть только на страницах, где
 * владелец разместил блоки портала; обычные пейволлы не задеты. Не отдельная
 * страница — часть портального контура.
 *
 * Ревизия 2 WEB-1152 (дефект 5): печатает приглашение РОВНО ОДИН блок —
 * тот, кого назначил провайдер (`portal.inviteHost`). Раньше текст и его
 * testid дублировали список и кнопка отмены, а форму рендерил только список:
 * на странице с обоими блоками абзац шёл дважды, на странице без списка —
 * обещание формы, которой рядом нет (тупик). Компонент `PortalInvite` ниже
 * собирает объяснение + форму в одно место, чтобы расхождение не вернулось.
 *
 * Anti-enum: ручка всегда отвечает 204, существование почты не
 * подтверждается — после отправки показываем честное «если такая почта
 * есть — письмо отправлено».
 */

const formStyle: React.CSSProperties = {
  marginBottom: 16,
  textAlign: 'left',
};

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #d9d9d9',
  fontSize: 14,
  marginTop: 8,
  boxSizing: 'border-box',
};

const submitStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: 8,
  padding: '10px 16px',
  borderRadius: 8,
  border: '1px solid #d9d9d9',
  background: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  fontSize: 14,
};

const noteStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#8c8c8c',
  marginTop: 8,
};

const otherEmailStyle: React.CSSProperties = {
  display: 'block',
  marginTop: 8,
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: 13,
};

const errorStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#cf1322',
  marginTop: 8,
};

export function PortalEntryRequestForm({
  language,
}: {
  /** WEB-1905: язык воронки — форма стоит на публичной странице покупателя. */
  language?: string | null;
}) {
  const t = getPublicQuizStrings(language).portal;
  const portal = usePortalActions();
  // Дефект 4: набранная почта и «письмо отправлено» живут в ПРОВАЙДЕРЕ.
  // Раньше они лежали в этом компоненте, то есть внутри блока-хозяина: стоило
  // составу видимых блоков поменяться (переход по экранам визарда), хозяин
  // менялся, форма размонтировалась — набранное пропадало, а человек отправлял
  // письмо повторно и первая, уже полученная, одноразовая ссылка сгорала.
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Вне портального провайдера (канвас редактора) форма не рисуется — блок
  // сам показывает инертное превью.
  if (!portal) return null;

  const email = portal.entryEmail;
  const setEmail = portal.setEntryEmail;
  const sent = portal.entryLinkSent;
  // WEB-3413: страница открыта по ссылке из письма после оплаты, номер
  // признан — известна только маска почты покупки.
  const masked = portal.entryMaskedEmail;

  if (sent) {
    return (
      <p data-testid="portal-entry-sent" style={noteStyle}>
        {masked ? t.entryNumberSent(masked) : t.entrySent}
      </p>
    );
  }

  if (masked) {
    const onRequestByNumber = async () => {
      if (busy) return;
      setBusy(true);
      setFailed(false);
      // WEB-3412: язык страницы портала — на нём уйдёт письмо со ссылкой.
      const ok = await portal.requestEntryByNumber(language ?? undefined);
      setBusy(false);
      if (ok) {
        portal.markEntryLinkSent();
      } else {
        setFailed(true);
      }
    };
    return (
      <div style={formStyle} data-testid="portal-entry-number">
        <p style={{ fontSize: 14 }}>{t.entryNumberPrompt}</p>
        <button
          type="button"
          style={{ ...submitStyle, opacity: busy ? 0.6 : 1 }}
          disabled={busy}
          onClick={() => void onRequestByNumber()}
          data-testid="portal-entry-number-submit"
        >
          {t.entryNumberSubmit(masked)}
        </button>
        {failed && <p style={errorStyle}>{t.entrySendError}</p>}
        <button
          type="button"
          style={otherEmailStyle}
          onClick={() => {
            setFailed(false);
            portal.forgetEntryNumber();
          }}
          data-testid="portal-entry-number-other"
        >
          {t.entryNumberOtherEmail}
        </button>
      </div>
    );
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setFailed(false);
    // WEB-3412: язык страницы портала — на нём уйдёт письмо со ссылкой.
    const ok = await requestPortalEntryLink(
      portal.projectId,
      trimmed,
      language ?? undefined,
    );
    setBusy(false);
    if (ok) {
      portal.markEntryLinkSent();
    } else {
      setFailed(true);
    }
  };

  return (
    <form style={formStyle} onSubmit={(event) => void onSubmit(event)}>
      <p style={{ fontSize: 14 }}>{t.entryPrompt}</p>
      <input
        type="email"
        // Гард WEB-733: у текстовых полей обязателен autoComplete.
        autoComplete="email"
        required
        placeholder={t.entryEmailPlaceholder}
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        style={inputStyle}
        data-testid="portal-entry-email"
      />
      <button
        type="submit"
        style={{ ...submitStyle, opacity: busy ? 0.6 : 1 }}
        disabled={busy}
        data-testid="portal-entry-submit"
      >
        {t.entrySubmit}
      </button>
      {failed && <p style={errorStyle}>{t.entrySendError}</p>}
    </form>
  );
}

/**
 * Дефект 5: объяснение «почему тут пусто» + форма запроса ссылки — одним
 * куском. Ревизия 5 (дефекты 2 и 3): рисуется, только если ЭТОТ ЭКЗЕМПЛЯР
 * блока получил право печатать приглашение (`active` = ответ
 * usePortalBlockPresence, синхронный уже на первом кадре); остальные
 * экземпляры в этом состоянии молчат.
 */
export function PortalInvite({
  active,
  language,
}: {
  active: boolean;
  /** WEB-1905: язык воронки — приглашение читает покупатель. */
  language?: string | null;
}) {
  const t = getPublicQuizStrings(language).portal;
  const portal = usePortalActions();
  if (!portal || !active) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      {/* WEB-3413: открыто по ссылке из письма и номер признан — пояснение
          даёт сама форма по номеру, «ссылка недействительна» здесь неправда. */}
      {!portal.entryMaskedEmail && (
      <p data-testid="portal-invalid-link" style={{ marginBottom: 12 }}>
        {/* WEB-1192 (п.3): протухшая сессия — своё честное объяснение. */}
        {portal.sessionExpired
          ? t.sessionExpired
          : portal.codeInvalid
            ? t.expiredCode
            : // WEB-1416 п.1: протухший/битый токен прямой manage_link.
              portal.guidInvalid
              ? t.expiredManageLink
              : t.invalidLink}
      </p>
      )}
      <PortalEntryRequestForm language={language} />
    </div>
  );
}
